import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const cellKey = (id, field) => `${id}|${field}`;

/** Ventiler and Konfigurasjon share one read: valves with live proposals, product types, variants and the sync state.
 * Every action returns the fresh view and the case context, so the two tabs and the calculation never drift apart.
 * Cell edits are optimistic: the sheet shows the value at once and a per-cell state (lagrer → lagret, or feil with the
 * message) tells what the server made of it – the table never jumps and never reverts in silence. */
export function useKonfig(project, ops, ctxKonfig) {
  const id = project.id;
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [cellStatus, setCellStatus] = useState({});
  const [lastSave, setLastSave] = useState(null);
  const timers = useRef({});
  const sig = JSON.stringify(ctxKonfig || null);
  const lastSig = useRef(sig);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${id}/konfig`);
      setView(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  // Uploads, undo, fact changes and edits elsewhere change the light block in the case context – follow it.
  useEffect(() => { if (lastSig.current !== sig) { lastSig.current = sig; load(); } }, [sig, load]);
  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

  const call = useCallback(async (label, promise, okMsg) => {
    setBusy(label);
    try {
      const r = await promise;
      if (r.data.konfig) setView(r.data.konfig);
      if (r.data.context || r.data.project) ops.apply(r.data);
      const msg = typeof okMsg === "function" ? okMsg(r.data) : okMsg;
      if (msg) toast.success(msg);
      return r.data;
    } catch (e) {
      toast.error(errMsg(e));
      throw e;
    } finally {
      setBusy(null);
    }
  }, [ops]);

  // Per-cell state. «saved» fades by itself; «error» stays until the cell is edited again.
  const mark = useCallback((keys, state, message) => {
    const at = Date.now();
    setCellStatus((s) => { const n = { ...s }; keys.forEach((k) => { if (state) n[k] = { state, message, at }; else delete n[k]; }); return n; });
    if (state === "saved") keys.forEach((k) => { clearTimeout(timers.current[k]); timers.current[k] = setTimeout(() => setCellStatus((s) => { if (s[k]?.state !== "saved") return s; const n = { ...s }; delete n[k]; return n; }), 1600); });
  }, []);
  const applyLocal = useCallback((cells) => setView((v) => {
    if (!v) return v;
    const byId = new Map();
    cells.forEach((c) => { if (!byId.has(c.id)) byId.set(c.id, []); byId.get(c.id).push(c); });
    return { ...v, valves: v.valves.map((x) => { const mine = byId.get(x.id); if (!mine) return x; const y = { ...x }; mine.forEach((c) => { y[c.field] = c.value; }); return y; }) };
  }), []);

  /** One cell: shown at once, confirmed (or refused) by the server. */
  const patchValve = useCallback(async (vid, field, value) => {
    const key = cellKey(vid, field);
    applyLocal([{ id: vid, field, value }]);
    mark([key], "saving");
    try {
      const r = await api.patch(`/projects/${id}/ventiler/${vid}`, { field, value });
      if (r.data.konfig) setView(r.data.konfig);
      if (r.data.context) ops.apply(r.data);
      mark([key], "saved");
      setLastSave({ at: Date.now(), ok: true, n: 1 });
      return r.data;
    } catch (e) {
      const msg = errMsg(e);
      mark([key], "error", msg);
      setLastSave({ at: Date.now(), ok: false, message: msg });
      load();
      throw e;
    }
  }, [id, ops, applyLocal, mark, load]);

  /** Many cells in one request – paste, fill-down, undo. Cells the server refuses are marked one by one; the rest land. */
  const patchCells = useCallback(async (cells) => {
    const keys = cells.map((c) => cellKey(c.id, c.field));
    applyLocal(cells);
    mark(keys, "saving");
    try {
      const r = await api.post(`/projects/${id}/ventiler/cells`, { cells: cells.map(({ id: vid, field, value }) => ({ id: vid, field, value })) });
      if (r.data.konfig) setView(r.data.konfig);
      if (r.data.context) ops.apply(r.data);
      const bad = new Map((r.data.errors || []).map((e) => [cellKey(e.id, e.field), e.message]));
      mark(keys.filter((k) => !bad.has(k)), "saved");
      bad.forEach((message, k) => mark([k], "error", message));
      setLastSave({ at: Date.now(), ok: !bad.size, n: r.data.updated, message: bad.size ? `${bad.size} ${bad.size === 1 ? "celle" : "celler"} ble avvist` : null });
      if (bad.size) toast.error(`${bad.size} ${bad.size === 1 ? "celle" : "celler"} ble avvist: ${[...bad.values()][0]}`);
      return r.data;
    } catch (e) {
      const msg = errMsg(e);
      mark(keys, "error", msg);
      setLastSave({ at: Date.now(), ok: false, message: msg });
      toast.error(msg);
      load();
      throw e;
    }
  }, [id, ops, applyLocal, mark, load]);

  const download = useCallback(async () => {
    setBusy("export");
    try {
      const r = await api.get(`/projects/${id}/ventiler/export.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ventiler-${project.number || id}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success("Ventillisten er lastet ned (xlsx) – lesbar eksport, ikke en COMOS-fil");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  }, [id, project.number]);

  return {
    view, loading, busy, cellStatus, lastSave, reload: load, download, patchValve, patchCells,
    run: (params, name) => call("run", api.post(`/projects/${id}/konfig/run`, { params: params || {}, name: name || "" }), (d) => `Variant «${(d.konfig.configurations.find((x) => x.id === d.config_id) || {}).name || name}» er kjørt`),
    rerun: (cfgId) => call("rerun", api.post(`/projects/${id}/konfig/${cfgId}/rerun`), "Konfigurasjonen er kjørt på nytt mot gjeldende ventilliste"),
    activate: (cfgId) => call("activate", api.post(`/projects/${id}/konfig/${cfgId}/activate`), "Varianten er nå den aktive"),
    rename: (cfgId, name) => call("rename", api.patch(`/projects/${id}/konfig/${cfgId}`, { name })),
    remove: (cfgId) => call("remove", api.delete(`/projects/${id}/konfig/${cfgId}`), "Varianten er slettet"),
    sync: (cfgId) => call("sync", api.post(`/projects/${id}/konfig/${cfgId}/sync`), (d) => d.sync?.label),
    bulk: (ids, field, value) => call("bulk", api.post(`/projects/${id}/ventiler/bulk`, { ids, field, value }), (d) => `${d.updated} ventiler oppdatert`),
    addValve: (tag, fields) => call("addValve", api.post(`/projects/${id}/ventiler`, { tag, fields }), `Ventil ${tag} lagt til`),
    removeValve: (vid) => call(`remove:${vid}`, api.delete(`/projects/${id}/ventiler/${vid}`), "Ventilen er fjernet"),
    approve: (vid, body, quiet) => call(`approve:${vid}`, api.post(`/projects/${id}/ventiler/${vid}/approve`, body), quiet ? null : body.approve ? ({ teknisk: "Teknisk godkjent – bundet til dette forslaget", verifisert: "Manuell verifikasjon registrert – dokumentgrunnlaget følger ventilen", kravavvik: "Kravavvik registrert med kundens aksept" }[body.kind] || "Godkjent") : "Godkjenningen er trukket tilbake"),
    // The person's own configuration: previews return data only (nothing saved); save/clear/apply return the fresh view.
    candidates: (vid, selection) => api.post(`/projects/${id}/ventiler/${vid}/kandidater`, { selection: selection || {} }).then((r) => r.data),
    previewSelection: (vid, selection) => api.post(`/projects/${id}/ventiler/${vid}/valg/preview`, { selection }).then((r) => r.data),
    setSelection: (vid, body) => call(`valg:${vid}`, api.put(`/projects/${id}/ventiler/${vid}/valg`, body), "Valget er lagret – kundekravet og systemets forslag står urørt"),
    clearSelection: (vid) => call(`valg:${vid}`, api.delete(`/projects/${id}/ventiler/${vid}/valg`), "Tilbake til systemets forslag"),
    reusePreview: (vid, ids) => api.post(`/projects/${id}/ventiler/${vid}/valg/like/preview`, { ids: ids || null }).then((r) => r.data),
    reuseApply: (vid, body) => call(`like:${vid}`, api.post(`/projects/${id}/ventiler/${vid}/valg/like`, body), (d) => `Konfigurasjonen er brukt på ${d.applied} ${d.applied === 1 ? "ventil" : "ventiler"}`),
  };
}
